/**
 * Cleanup for the move to a shared Cognito cookie domain.
 * Keep until old releases can no longer recreate cookies at narrower scopes.
 */

const AUTH_KEY_PREFIXES = [
  'CognitoIdentityServiceProvider.',
  'amplify-',
  'aws-amplify-',
  'com.amplify.',
]

/** Amplify v5, which this app uses, writes these unprefixed. */
const UNPREFIXED_AUTH_KEYS = ['oauth_state', 'ouath_pkce_key']

/** Recognizes Cognito session keys and legacy Amplify OAuth state. */
const isAuthKey = (key: string): boolean =>
  AUTH_KEY_PREFIXES.some((prefix) => key.startsWith(prefix)) || UNPREFIXED_AUTH_KEYS.includes(key)

/** `.example.com` and `example.com` are the same scope per RFC 6265. */
const withoutLeadingDot = (domain: string): string => domain.replace(/^\./, '')

export interface SweepLocation {
  hostname: string
  pathname: string
  protocol: string
}

/**
 * Domain scopes to clear, from `hostname` up to but excluding `targetDomain`.
 *
 * Excluding the target is what stops the sweep deleting the shared cookie itself.
 * `undefined` is the host-only scope, written with no `Domain` attribute.
 *
 * With no target there is no migration to make, and the host-only scope is then
 * where the app's own cookie lives, so nothing may be swept. Returning the
 * host-only scope here would delete the live session on every load.
 *
 * @param hostname - the current host
 * @param targetDomain - the configured shared cookie domain, if any
 */
export const legacyCookieScopes = (
  hostname: string,
  targetDomain: string | undefined
): (string | undefined)[] => {
  if (!targetDomain) {
    return []
  }

  const target = withoutLeadingDot(targetDomain).toLowerCase()
  const host = hostname.toLowerCase()
  // Only a strict parent is a migration target. With an equal domain, a
  // host-only deletion can remove the live cookie too. Unrelated domains must
  // not trigger cleanup when a deployment has the wrong configuration.
  if (!target.includes('.') || !host.endsWith(`.${target}`)) {
    return []
  }
  const labels = host.split('.')
  const scopes: (string | undefined)[] = [undefined]

  // Stops before the last label so a public suffix is never a candidate.
  for (let i = 0; i < labels.length - 1; i++) {
    const scope = labels.slice(i).join('.')
    if (scope === target) {
      break
    }
    scopes.push(scope)
  }

  return scopes
}

/** Path prefixes a cookie could be scoped to, widest first. */
const cookiePaths = (pathname: string): string[] => {
  const segments = pathname.split('/').filter(Boolean)
  return ['/', ...segments.map((_, i) => `/${segments.slice(0, i + 1).join('/')}`)]
}

/** Lists visible auth cookie names once, including names present at multiple scopes. */
const authCookieNames = (): string[] => {
  const names = document.cookie.split(';').map((cookie) => cookie.split('=')[0]?.trim() ?? '')
  return [...new Set(names.filter(isAuthKey))]
}

/**
 * Deletes auth cookies left at scopes narrower than the shared cookie domain.
 *
 * A host-scoped cookie and a parent-scoped one coexist under the same name, and
 * `document.cookie` reports no scope to tell them apart, so Amplify can read the
 * stale one and pin the user to a previous session.
 *
 * Runs on every boot rather than once: while either product is still un-migrated
 * it keeps writing host-scoped cookies, so a once-only flag would let the
 * shadowing come back.
 *
 * @param targetDomain - the configured shared cookie domain, if any
 * @param location - defaults to `window.location`
 */
export const sweepLegacyAuthCookies = (
  targetDomain: string | undefined,
  location?: SweepLocation
): void => {
  if (typeof document === 'undefined' || typeof window === 'undefined') {
    return
  }

  const names = authCookieNames()
  if (names.length === 0) {
    return
  }

  const { hostname, pathname, protocol } = location ?? window.location
  const scopes = legacyCookieScopes(hostname, targetDomain)
  const paths = cookiePaths(pathname)
  const secure = protocol === 'https:' ? '; Secure' : ''

  for (const name of names) {
    for (const path of paths) {
      for (const domain of scopes) {
        const scope = domain ? `; domain=${domain}` : ''
        document.cookie = `${name}=; Max-Age=0; path=${path}${scope}; SameSite=Lax${secure}`
      }
    }
  }
}

/**
 * Removes Amplify auth state from web storage.
 *
 * Each getter is read inside the `try`: reaching for `localStorage` throws
 * `SecurityError` where site data is blocked, and this runs during module
 * evaluation, so an escaping throw would take down app startup.
 */
export const sweepLegacyAuthStorage = (): void => {
  if (typeof window === 'undefined') {
    return
  }

  for (const getStore of [() => window.localStorage, () => window.sessionStorage]) {
    try {
      const store = getStore()
      Object.keys(store)
        .filter(isAuthKey)
        .forEach((key) => store.removeItem(key))
    } catch {
      // Storage unavailable, so there is nothing to clean.
    }
  }
}

/**
 * Earlier parent-domain experiments did not record the SDK's clock-drift format.
 * Purge that ambiguous session once before adopting the shared v5 storage format.
 * The marker is shared by both products and survives normal SDK sign-out.
 */
export const initializeSharedCognitoStorage = (
  targetDomain: string | undefined,
  clientId: string | undefined,
  location?: SweepLocation
): void => {
  if (typeof document === 'undefined' || typeof window === 'undefined' || !clientId) {
    return
  }
  const { hostname, protocol } = location ?? window.location
  if (
    targetDomain !== 'motif.io' ||
    (hostname !== targetDomain && !hostname.endsWith(`.${targetDomain}`))
  ) {
    return
  }

  const marker = `motif.cognito.${clientId}.sharedCookiesVersion`
  if (document.cookie.split(';').some((cookie) => cookie.trim() === `${marker}=1`)) {
    return
  }
  const attributes = `path=/; domain=${targetDomain}; SameSite=Lax${protocol === 'https:' ? '; Secure' : ''}`
  const prefix = `CognitoIdentityServiceProvider.${clientId}.`
  for (const name of authCookieNames().filter((name) => name.startsWith(prefix))) {
    document.cookie = `${name}=; Max-Age=0; ${attributes}`
  }
  document.cookie = `${marker}=1; Max-Age=31536000; ${attributes}`
}
